import React, { FC, useEffect, useState } from 'react';
import { deviceCounterQueryClient } from '../../../utils/ReactQueryClients';
import { addSeconds, format, parseISO, subSeconds } from 'date-fns';
import { Port } from '@thingslog/repositories';
import { SensorUtils } from '@thingslog/utils';

export const DeviceLocationTooltip: FC<Props> = ({
  deviceNumber,
  timestamp,
  ports,
  location,
}: Props) => {
  const { useDeviceReadings } = deviceCounterQueryClient;
  const [sensorReadings, setSensorReadings] = useState<SensorReading[]>([]);

  const outputFormat = "yyyy-MM-dd'T'HH:mm:ss";
  const localTimestamp = timestamp.slice(0, 19);
  const originalDate = parseISO(localTimestamp);
  const timeBefore = subSeconds(originalDate, 1);
  const timeAfter = addSeconds(originalDate, 1);
  const formattedBefore = format(timeBefore, outputFormat);
  const formattedAfter = format(timeAfter, outputFormat);
  const formattedOriginal = format(originalDate, 'dd/MM/yyyy HH:mm:ss');

  const { data: readings, isLoading } = useDeviceReadings(
    deviceNumber,
    formattedBefore,
    formattedAfter,
    1,
    { refetchOnWindowFocus: false }
  );

  useEffect(() => {
    if (!readings) return;
    const displayedSensorReading: SensorReading[] = [];
    const sensorIds = Object.keys(readings);
    sensorIds.forEach((sensorId: string) => {
      try {
        const foundPort: Port | null = ports?.[parseInt(sensorId)] || null;
        const sensor = foundPort?.sensor || null;
        const currSensorReadings = readings[sensorId];
        const hasReadings = currSensorReadings.length > 0;
        if (hasReadings) {
          const lastReading = currSensorReadings[currSensorReadings.length - 1];
          const sensorIndex = parseInt(sensorId);
          displayedSensorReading.push({
            sensorIndex,
            reading: lastReading.reading,
            unit: sensor ? SensorUtils({ sensor }).getSensorUnit() : null,
            sensorName: sensor?.name || `Sensor ${sensorIndex}`,
          });
        }
      } catch {
        // TODO: Handle error
        console.error(`Error parsing sensor index for sensor ${sensorId}`);
      }
    });
    setSensorReadings(displayedSensorReading);
  }, [readings, ports]);

  return (
    <div className="w-[17rem]">
      {isLoading && <div className="p-2 text-center text-sm text-slate-500">Loading...</div>}
      {!isLoading && sensorReadings.length === 0 && (
        <div className="p-2 text-center text-sm text-slate-500">No readings found</div>
      )}

      {sensorReadings.length > 0 && (
        <div className="flex max-h-[12rem] flex-col overflow-y-auto">
          {sensorReadings.map((sr: SensorReading) => (
            <div
              key={`${deviceNumber}-${sr.sensorIndex}-${timestamp}`}
              className="flex cursor-pointer items-center justify-between border-b border-slate-200 p-2 transition-colors last:border-b-0 hover:bg-slate-50"
            >
              <div>
                <div className="text-sm font-semibold text-slate-800">{sr.sensorName}</div>
                {sr.unit && <div className="text-xs text-slate-500">Unit: {sr.unit}</div>}
              </div>

              <div className="text-base font-bold text-slate-900">{sr.reading}</div>
            </div>
          ))}
        </div>
      )}
      <div className="flex items-center justify-between border-t border-slate-200 p-2 text-xs">
        <span className="text-slate-400">
          {location.lat.toFixed(6)}, {location.lng.toFixed(6)}
        </span>
        <span className="text-slate-400">{formattedOriginal}</span>
      </div>
    </div>
  );
};

interface SensorReading {
  sensorIndex: number;
  reading: number;
  sensorName: string;
  unit: string | null;
}

interface Props {
  deviceNumber: string;
  timestamp: string;
  ports?: Record<number, Port>;
  location: { lat: number; lng: number };
}
